import { Component, Input, OnInit } from '@angular/core';
import { EMPTY, Subscription, forkJoin } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { expand, finalize, pairwise } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';

import {
  ChartDataset,
  ChartDatasetFilter,
  ChartDatasetFilterOption,
  ChartLabel,
} from 'src/app/core/entities/chart-dataset';
import {
  ChartDescription,
  ChartDescriptionFilter,
} from 'src/app/core/entities/chart-description';
import { WalkingAid, walkingAidMapping } from 'src/app/core/enums/walking-aid';
import { AutoUnsub } from 'src/app/core/decorators/auto-unsub.decorator';
import { ChartDataPoint } from 'src/app/core/entities/chart-data-point.entity';
import { ChartEvaluation } from 'src/app/core/entities/chart-evaluation';
import { ChartService } from 'src/app/core/services/chart.service';
import { EvaluationService } from 'src/app/core/services/evaluation.service';
import { Feature } from 'src/app/core/entities/feature.entity';
import { FeatureService } from 'src/app/core/services/feature.service';
import { Patient } from 'src/app/core/entities/patient.entity';

@Component({
  selector: 'app-endurance',
  templateUrl: './endurance.component.html',
  styleUrls: ['./endurance.component.css'],
})
@AutoUnsub()
export class EnduranceComponent implements OnInit {
  @Input() public patientProfile: Patient = new Patient();
  private _observers: Subscription = new Subscription();
  private _features: Feature[] = [];
  public evaluation: ChartEvaluation = {
    evaluationCode: EvaluationService.EVALUATION_6MWT,
    features: [
      {
        featureId: FeatureService.FEATURE_6MWT_6MIN_WD,
        label: $localize`:@@patient_evaluationEndurance_feature6MWT6MinWDLabel:Distance`,
        min: 0,
        max: 1800,
        isMaxLimited: true,
        chartDatasetOption: {
          type: 'line',
          color: '#0e5cdd',
          useTickModel: true,
          tooltip: {
            label: $localize`:@@patient_evaluationEndurance_feature6MWT6MinWDTooltipLabel:Distance traveled in 6 min (in m)`,
            unit: 'm',
          },
          hidden: true,
          pointStyle: 'circle',
        },
      },
      {
        featureId: FeatureService.FEATURE_6MWT_6MIN_WS_MEAN,
        label: $localize`:@@patient_evaluationEndurance_feature6MWT6MinWSMeanLabel:Average speed in 6 min (in km/h)`,
        min: 0,
        max: 5,
        isMaxLimited: true,
        chartDatasetOption: {
          type: 'line',
          color: '#6225e1',
          tooltip: {
            label: $localize`:@@patient_evaluationEndurance_feature6MWT6MinWSMeanTooltipLabel:Average speed`,
            unit: 'km/h',
          },
          pointStyle: 'circle',
          axis: {
            id: '6mwt6minWsMean',
            type: 'linear',
            position: 'right',
            beginAtZero: true,
          },
        },
      },
      {
        featureId: FeatureService.FEATURE_6MWT_TWT,
        label: $localize`:@@patient_evaluationEndurance_feature6MWTTWTLabel:Duration (in min)`,
        min: 0,
        max: 6,
        chartDatasetOption: {
          type: 'line',
          color: '#0dc49c',
          tooltip: {
            label: $localize`:@@patient_evaluationEndurance_feature6MWTTWTTooltipLabel:Duration`,
            unit: 'min',
            pattern: /([0-9]*)\.([0-9]*)/,
            replace: '$1, $2',
          },
          useTickModel: false,
          hidden: true,
          pointStyle: 'circle',
          axis: {
            id: 'perimeterTwt',
            type: 'linear',
            position: 'right',
            beginAtZero: true,
          },
        },
      },
    ],
  };
  public datasetFilter: ChartDatasetFilter = {
    title: $localize`:@@patient_evaluationEndurance_feature6MWTWATitle:Filtering data by type of aid used`,
    legend: $localize`:@@patient_evaluationEndurance_feature6MWTWALegend:Realized evaluation with walking aid`,
    featureId: FeatureService.FEATURE_6MWT_WA,
    pointStyle: 'triangle',
    options: [
      {
        title: $localize`:@@patient_evaluationEndurance_feature6MWTWANoHelpTitle:Show performances with no help`,
        label: walkingAidMapping[WalkingAid.NO_HELP],
        value: WalkingAid.NO_HELP,
        order: 0,
        hideOnTooltip: true,
      },
      {
        title: $localize`:@@patient_evaluationEndurance_feature6MWTWAUnilateralHelpTitle:Show performances with unilateral help`,
        label: walkingAidMapping[WalkingAid.UNILATERAL_HELP],
        value: WalkingAid.UNILATERAL_HELP,
        order: 1,
        pointStyle: 'triangle',
      },
      {
        title: $localize`:@@patient_evaluationEndurance_feature6MWTWABilateralHelpTitle:Show performances with bilateral help`,
        label: walkingAidMapping[WalkingAid.BILATERAL_HELP],
        value: WalkingAid.BILATERAL_HELP,
        order: 2,
        pointStyle: 'triangle',
      },
      {
        title: $localize`:@@patient_evaluationEndurance_feature6MWTWAAccompanyingPersonTitle:Show performances with accompanying person`,
        label: walkingAidMapping[WalkingAid.ACCOMPANYING_PERSON],
        value: WalkingAid.ACCOMPANYING_PERSON,
        order: 3,
        pointStyle: 'triangle',
      },
    ],
  };
  public datasets: ChartDataset[] = [];
  public xLabels: ChartLabel[] = [];
  public chartDescription: ChartDescription;
  public dateRangeForm: FormGroup;
  public loading = true;
  public loadingChart = true;

  constructor(
    private featureService: FeatureService,
    private chartService: ChartService,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.prepareDateRangeForm();

    this.resetChartDescription();
  }

  prepareDateRangeForm(): void {
    const startDate = moment.utc().subtract(6, 'months').format('MM/YYYY');
    const endDate = moment.utc().format('MM/YYYY');

    this.dateRangeForm = this.formBuilder.group({
      startDate: [
        startDate,
        [
          Validators.required,
          Validators.pattern(/^(0[1-9]|1[012])\/[0-9]{4}$/),
        ],
      ],
      endDate: [
        endDate,
        [
          Validators.required,
          Validators.pattern(/^(0[1-9]|1[012])\/[0-9]{4}$/),
        ],
      ],
    });

    this._observers.add(
      this.dateRangeForm.valueChanges.pipe(pairwise()).subscribe({
        next: ([previousRes, res]) => this.submitDateRangeForm(),
      })
    );

    this.loading = false;
  }

  changeDateRange(
    event: { monthIndex: number; year: number },
    key: string
  ): void {
    const value = moment
      .utc([event.year, event.monthIndex, 1])
      .format('MM/YYYY');

    this.dateRangeForm.get(key).setValue(value);
  }

  getDateYear(key: string): number {
    let splitDate = this.dateRangeForm.get(key).value.split('/');
    return splitDate[1];
  }

  getDateMonth(key: string): number {
    let splitDate = this.dateRangeForm.get(key).value.split('/');
    return parseInt(splitDate[0], 10) - 1;
  }

  submitDateRangeForm(): void {
    if (this.dateRangeForm.valid) {
      this.loadingChart = true;
      this.setChart();
    }
  }

  resetChartDescription(): void {
    this.chartDescription = {
      evaluationCode: this.evaluation.evaluationCode,
      evaluationLabel: $localize`:@@patient_evaluationEndurance_chartDescriptionLabel:of walking endurance`,
      measuringPointsTotal: 0,
      featuresGroups: [
        {
          label: '',
          features: [],
        },
      ],
      filters: [],
      resume: [
        {
          filterValue: [0],
          label: $localize`:@@patient_evaluationEndurance_chartDescriptionResumeNoWalkingAidLabel:No walking aid`,
        },
        {
          filterValue: [1, 2, 3],
          label: $localize`:@@patient_evaluationEndurance_chartDescriptionResumeWalkingAidLabel:With walking aid`,
        },
      ],
    };

    this.datasetFilter.options.forEach((option) => {
      this.chartDescription.filters.push(<ChartDescriptionFilter>{
        label: option.label,
        featureId: this.datasetFilter.featureId,
        value: option.value,
      });
    });
  }

  setChart(): void {
    this._features = [];
    this.datasets = [];
    this.xLabels = [];

    // Reset chart description
    this.resetChartDescription();

    const startDate = moment.utc(
      this.dateRangeForm.get('startDate').value,
      'MM/YYYY'
    );
    const endDate = moment.utc(
      this.dateRangeForm.get('endDate').value,
      'MM/YYYY'
    );

    let params = {
      page: 1,
      startDate: startDate.toISOString(),
      endDate: endDate.endOf('month').toISOString(),
      patientUuid: this.route.snapshot.params['uuid'],
      featureIds: this.evaluation.features
        .map((item) => item.featureId)
        .concat(this.datasetFilter.featureId),
    };

    const source$ = this.featureService.getFeatures(params).pipe(
      expand((res) => {
        params.page++;
        this._features = this._features.concat(res);
        return res.length < 50
          ? EMPTY
          : this.featureService.getFeatures(params);
      })
    );

    forkJoin([source$])
      .pipe(
        finalize(() => {
          this.loading = false;
          this.loadingChart = false;
        })
      )
      .subscribe((res) => {
        let chartDataPoints = {};

        this.evaluation.features.forEach((feature) => {
          let groupedFeatures = this.featureService.groupFeatures(
            this._features,
            feature.featureId
          );
          chartDataPoints[feature.featureId] =
            this.chartService.setChartDataPoints(groupedFeatures);
        });

        if (Object.values(chartDataPoints).length > 0) {
          // Set labels
          const maxDataPoints = Object.values(chartDataPoints).reduce(
            (r: any, s: any) => (r.length > s.length ? r : s),
            0
          );
          this.xLabels = this.chartService.setXLabels(
            maxDataPoints as ChartDataPoint[]
          );

          // Add filter features to datasets
          let groupedFilterFeatures = this.featureService.groupFeatures(
            this._features,
            this.datasetFilter.featureId
          );
          let chartDataFilterPoints =
            this.chartService.setChartDataFilterPoints(groupedFilterFeatures);

          // Set datasets
          this.evaluation.features.forEach((feature) => {
            let dataset = this.chartService.setDataset(
              feature,
              chartDataPoints[feature.featureId],
              feature.chartDatasetOption,
              chartDataFilterPoints,
              this.xLabels,
              feature.min ??
                (chartDataPoints[feature.featureId].length
                  ? chartDataPoints[feature.featureId][0].min
                  : null),
              feature.max ??
                (chartDataPoints[feature.featureId].length
                  ? chartDataPoints[feature.featureId][0].max
                  : null),
              feature.isMaxLimited
            );

            this.datasets.push(dataset);
            this.chartDescription.featuresGroups[0].features.push({
              featureId: feature.featureId,
              hidden: feature.chartDatasetOption.hidden,
              data: dataset.data,
              unit: feature.chartDatasetOption.tooltip
                ? feature.chartDatasetOption.tooltip.unit
                : '',
              label: Array.isArray(feature.label)
                ? feature.label[0]
                : feature.label,
            });
          });
          this.chartDescription.featuresGroups[0].features.forEach(
            (dataset, i) => {
              if (
                this.chartDescription.featuresGroups[0].features[i].data.filter(
                  (item) => item.value !== null
                ).length > this.chartDescription.measuringPointsTotal
              ) {
                this.chartDescription.measuringPointsTotal =
                  this.chartDescription.featuresGroups[0].features[
                    i
                  ].data.filter((item) => item.value !== null).length;
              }
            }
          );
        }
      });
  }

  updateChartDescriptionFeature(chartDataset: ChartDataset): void {
    let feature = this.chartDescription.featuresGroups[0].features.find(
      (item) => item.featureId == chartDataset.id
    );
    feature.hidden = chartDataset.hidden;
  }

  updateChartDescriptionFilter(
    chartDatasetFilterOption: ChartDatasetFilterOption
  ): void {
    let filter = this.chartDescription.filters.find(
      (item) => item.value == chartDatasetFilterOption.value
    );
    filter.hidden = !chartDatasetFilterOption.show;
  }
}
